import dataProvider from "./data/dataProvider";
import {
  getRolePowers,
  getUserID,
  onUserLogout,
  setTokenUuid,
  getUserChecks,
  getPermissions,
  getSubscriptionPermission,
  getMvpleadData,
} from "./utils/AuthUtil";
import { is_empty } from "./utils/validations";
import { getParameterByName } from "./utils/Utils";
import { api } from "data";
import posthog from "posthog-js";
import { logError } from "utils/error";
import { fetchCreatorConfig } from "features/Common/modules/CreatorData/utils/CreatorData.utils";
import { JWT_GRANT_TYPES } from "data/api.constants";
import { initializeFCM } from "features/Firebase/utils/Firebase.utils";
import { USER_LOGIN_CUSTOM_EVENT } from "features/Common/constants/customEvent.constants";
import { baseApiClient } from "data/modules/api/api.client";
import { USER_ORG_LOGIN_LIST } from "features/Onboarding/constants/Onboarding.apis";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { checkIsStringEmpty } from "features/Common/modules/DataTypes/Strings.utils";
import { LOGIN_GRANT_TYPES } from "features/Onboarding/constants/Onboarding.constants";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "features/Common/modules/Storage/modules/LocalStorage/utils/LocalStorage.utils";
import { LOCAL_STORAGE_KEYS } from "features/Common/modules/Storage/modules/LocalStorage/constants/LocalStorage.constants";

export const identifyUser = async (userId) => {
  try {
    const isStaff =
      getLocalStorageItem(LOCAL_STORAGE_KEYS.IS_STAFF) ||
      getLocalStorageItem(LOCAL_STORAGE_KEYS.BD_LOGIN)
        ? true
        : false;
    const staffEmail = getLocalStorageItem(LOCAL_STORAGE_KEYS.BD_EMAIL);

    let mvpLeadData = await getMvpleadData();
    let payload = {
      user_id: userId,
      is_staff: isStaff,
      staff_email: staffEmail,
    };

    if (!is_empty(mvpLeadData)) {
      delete mvpLeadData.creator__phone_number; // do not want creator phone number to be sent to posthog
      Object.assign(payload, mvpLeadData);
    }

    const userIdentity =
      getLocalStorageItem(LOCAL_STORAGE_KEYS.USER_IDENTITY) || {};
    if (!userIdentity.isIdentified || isStaff !== userIdentity.isStaff) {
      posthog.identify(userId, payload);
    }
    const user_identity = {
      isStaff,
      isIdentified: true,
    };
    setLocalStorageItem(LOCAL_STORAGE_KEYS.USER_IDENTITY, user_identity);
  } catch (error) {
    logError({
      error,
      occuredAt: "identifyUser in src/authProvider.js",
      when: "calling getMvpLeadData",
    });
  }
};

export const onUserLogin = async ({
  username,
  grantType = JWT_GRANT_TYPES.AUTHORIZATION_CODE,
  email,
  creator_username,
}) => {
  let userUuid = username;
  let creatorUsername = creator_username;
  // if profile is not already selected
  if (checkIsStringEmpty(creatorUsername)) {
    const orgResponse = await baseApiClient.post(USER_ORG_LOGIN_LIST, {
      client_id: username,
      email,
    });
    const orgList = orgResponse.data.data.creator_profiles_for_member_orgs;
    // if user has more than 1 profile
    if (!checkIsArrayEmpty(orgList)) {
      if (orgList.length > 1) {
        return {
          profiles: orgList,
        };
      }
      if (orgList.length === 1) {
        creatorUsername = orgList[0].username;
      }
    }
  }
  setTokenUuid(userUuid);
  setLocalStorageItem(LOCAL_STORAGE_KEYS.CREATOR_USERNAME, creatorUsername);

  const uuid_status = await dataProvider.fetchJwt({
    grant_type: grantType,
  });
  if (uuid_status) {
    const loginDataPromises = [
      getUserChecks(),
      getPermissions(),
      getRolePowers(),
      getSubscriptionPermission(),
      fetchCreatorConfig(),
    ];
    Promise.all(loginDataPromises)
      .then(() => {
        // request for notification permission on user login / signup
        initializeFCM();

        // Dispatching a custom event on user login to notify listeners (currently used in the useSSE hook)
        const event = new CustomEvent(USER_LOGIN_CUSTOM_EVENT);
        window.dispatchEvent(event);

        return true;
      })
      .catch((error) => {
        logError({
          error,
          occuredAt: "src/authProvider.js",
          when: "onUserLogin̉",
        });
      });
  } else {
    throw new Error("uuid_status not found");
  }
};

const authProvider = {
  login: ({ grantType, loginPayload }) => {
    const payload = loginPayload;
    const ref =
      getParameterByName("ref") || window.sessionStorage.getItem("ref");
    if (!is_empty(ref)) {
      payload.ref = ref;
    }
    switch (grantType) {
      case LOGIN_GRANT_TYPES.google:
        return dataProvider
          .custom_request(api.post_google_login, "POST", payload)
          .then(async (data) => {
            if (
              data &&
              data.data &&
              data.data.status &&
              data.data.status !== "success"
            ) {
              return { status: data.data.status };
            }
            const userLoginResponse = await onUserLogin({
              username: data?.data?.user_ref?.username,
              email: data?.data?.user_ref?.email,
              creator_username: loginPayload.creator_username,
            });
            return {
              newUser: data.data.new_user_flag,
              status: data.data.status,
              profiles: userLoginResponse?.profiles,
            };
          })
          .catch((error) => {
            return {
              error: error?.body?.data?.message,
              status: error?.body?.status,
              signup_status: error?.body?.data?.status,
            };
          });
      case LOGIN_GRANT_TYPES.emailNew:
        return dataProvider
          .custom_request(api.create_new_user, "POST", payload)
          .then(async (data) => {
            if (
              data &&
              data.data &&
              data.data.status &&
              data.data.status === "failure"
            ) {
              return { status: data.data.status, error: data.data.error };
            }
            await onUserLogin({
              username: data?.data?.userId,
              email: loginPayload.email,
              creator_username: data?.data?.userId,
            });
            return {
              newUser: 1,
              error: data.data.error,
              status: data.data.status,
            };
          })
          .catch((error) => {
            return {
              error: error?.body?.message || error?.body?.data?.error,
              status: error?.body?.status,
            };
          });
      default:
        return dataProvider
          .custom_request(api.call_existing_user, "POST", loginPayload)
          .then(async (data) => {
            if (
              data &&
              data.data &&
              data.data.status &&
              data.data.status === "failure"
            ) {
              return { status: data.data.status, error: data.data.error };
            }

            const userLoginResponse = await onUserLogin({
              username: data?.data?.userId,
              email: loginPayload.email,
              creator_username: loginPayload.creator_username,
            });
            return {
              newUser: 0,
              error: data.data.error,
              status: data.data.status,
              profiles: userLoginResponse?.profiles,
            };
          })
          .catch((error) => {
            return {
              error: error?.body?.data?.error || error?.body?.message,
              status: error?.body?.status,
            };
          });
    }
  },
  logout: async () => {
    await dataProvider.custom_request(
      `notifications/exly/webpush/logout`,
      "POST"
    );
    onUserLogout(false);
    return Promise.resolve();
  },
  checkError: (data) => {
    const status = data.status;
    const code = data.body.status;

    if (
      status === 401 ||
      status === 403 ||
      (code && status === 400 && (code === 6 || code === 3)) // TODO: what does values of `code` represent?
    ) {
      onUserLogout(true);
      return Promise.reject();
    }
    return Promise.resolve();
  },
  checkAuth: () => {
    return !is_empty(getUserID()) ? Promise.resolve() : Promise.reject();
  },
  getPermissions: () => Promise.reject("Unknown method"),
  getIdentity: () => {
    try {
      const { first_name, display_image } = JSON.parse(
        getLocalStorageItem("auth")
      );
      return Promise.resolve({
        id: getLocalStorageItem(LOCAL_STORAGE_KEYS.UUID),
        fullName:
          window.screen.width < 780
            ? ""
            : `${first_name.substring(0, 9)}${
                first_name.length > 9 ? "..." : ""
              }`,
        avatar: display_image,
      });
    } catch (error) {
      return Promise.resolve({
        id: getLocalStorageItem(LOCAL_STORAGE_KEYS.UUID),
        fullName: "",
        avatar: null,
      });
    }
  },
};

export default authProvider;
